import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { Alert, ReportData } from '../data/types';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { fmtDate } from './formatUtils';
import { colors, fontFamily, severity, space, typeScale } from '../data/designTokens';
import type { Severity } from '../data/designTokens';

const SEV_ORDER: Severity[] = ['SEVERE', 'HIGH', 'MEDIUM', 'LOW'];

type Bucket = { severity: Severity; count: number; topCategories: Array<{ name: string; count: number }> };

const bucketize = (alerts: Alert[]): Bucket[] =>
    SEV_ORDER.map((sev) => {
        const matching = alerts.filter((a) => a.severity === sev);
        const catCounts = matching.reduce<Record<string, number>>((acc, a) => {
            const k = a.category || 'Uncategorised';
            acc[k] = (acc[k] ?? 0) + 1;
            return acc;
        }, {});
        const topCategories = Object.entries(catCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([name, count]) => ({ name, count }));
        return { severity: sev, count: matching.length, topCategories };
    });

const sevSortRank = (s: Severity) => SEV_ORDER.indexOf(s);
const sortedAlerts = (alerts: Alert[]): Alert[] =>
    [...alerts].sort((a, b) => {
        const byRank = sevSortRank(a.severity) - sevSortRank(b.severity);
        if (byRank !== 0) return byRank;
        const ad = Date.parse(a.alertDate) || 0;
        const bd = Date.parse(b.alertDate) || 0;
        return bd - ad;
    });

const resolveEntityLabel = (a: Alert, assesseeName?: string): { name?: string; type?: string } => {
    if (a.entityName?.trim()) return { name: a.entityName.trim(), type: a.entityType };
    if (!a.source || a.source === 'COMPANY') return { name: assesseeName, type: 'Company' };
    return { name: undefined, type: a.entityType };
};

// ---------- Screen ----------
const ScreenSeverityBucket: React.FC<{ bucket: Bucket }> = ({ bucket }) => {
    const sev = severity[bucket.severity];
    return (
        <div
            style={{
                flexGrow: 1,
                flexBasis: '22%',
                minWidth: 160,
                padding: 14,
                borderRadius: 4,
                background: sev.bg,
                border: `1px solid ${sev.fg}`,
            }}
        >
            <div style={{ fontSize: '0.7rem', color: sev.fg, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
                {sev.label}
            </div>
            <div
                style={{
                    fontSize: '1.9rem',
                    fontFamily: "'Geist Mono', monospace",
                    fontWeight: 500,
                    color: sev.fg,
                    lineHeight: 1,
                    marginTop: 4,
                }}
            >
                {bucket.count}
            </div>
            <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 3 }}>
                {bucket.count === 1 ? 'alert raised' : 'alerts raised'}
            </div>
        </div>
    );
};

const ScreenAlertCard: React.FC<{ a: Alert; assesseeName?: string }> = ({ a, assesseeName }) => {
    const sev = severity[a.severity];
    const entity = resolveEntityLabel(a, assesseeName);
    return (
        <div
            style={{
                border: `1px solid ${colors.rule}`,
                borderLeft: `2px solid ${sev.fg}`,
                borderRadius: 4,
                padding: '12px 14px',
                background: colors.paper,
                marginBottom: 10,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ScreenChip tone={a.severity}>{sev.label}</ScreenChip>
                    <span
                        style={{
                            fontSize: '0.7rem',
                            color: colors.ink40,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                        }}
                    >
                        {a.category || 'Alert'}
                    </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: colors.ink40, fontFamily: "'Geist Mono', monospace" }}>
                    {fmtDate(a.alertDate)}
                </span>
            </div>
            {entity.name ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.92rem', fontWeight: 600, color: colors.ink00 }}>{entity.name}</span>
                    {entity.type ? (
                        <span
                            style={{
                                fontSize: '0.66rem',
                                color: colors.ink40,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}
                        >
                            · {entity.type}
                        </span>
                    ) : null}
                </div>
            ) : null}
            <div style={{ fontSize: '0.88rem', color: colors.ink10, lineHeight: 1.5 }}>{a.description}</div>
        </div>
    );
};

const ScreenAlerts: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const a = data.alerts;
    if (!a) return null;
    const buckets = bucketize(a.alerts);
    const alerts = sortedAlerts(a.alerts).slice(0, 20);
    return (
        <ScreenSection
            kicker="Signals"
            title="Alerts & anomalies"
            subtitle={`${a.totalRecords} total alert${a.totalRecords === 1 ? '' : 's'} raised across the assessee timeline`}
            index={index}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {buckets.map((b) => (
                    <ScreenSeverityBucket key={b.severity} bucket={b} />
                ))}
            </div>
            <div>
                {alerts.map((x, i) => (
                    <ScreenAlertCard key={i} a={x} assesseeName={data.identity.orgName} />
                ))}
            </div>
        </ScreenSection>
    );
};

export const alertsModule: ModuleDefinition = {
    meta: {
        id: 'alerts',
        title: 'Alerts & anomalies',
        description: 'Severity-bucketed alerts with category and reason per card.',
        dataSource: 'Alerts API',
    },
    hasData: (d) => !!d.alerts && d.alerts.alerts.length > 0,
    Screen: ScreenAlerts,
};
